import React, { ReactElement, useContext, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useTracking } from "../analytics/useTracking";
import { Category, Action } from "../analytics/Tracking";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import CreateRoom from "../features/CreateRoom";
import Room from "../features/Room";
import { Routes, Route, useParams, useLocation } from "react-router-dom";
import { ContextPlayer, PlayerContext } from "../contexts/PlayerContext";
import JoinRoom from "../features/JoinRoom";
import { SessionStorage } from "../localstorage/SessionStorage";
import { RoomParams } from "../types/RouterTypes";
import { UserMenu } from "./UserMenu";
import About from "../main/About";
import AboutButton from "../components/AboutButton";
import SignUp from "../auth/SignUp";
import SignIn from "../auth/SignIn";
import RecentRooms from "../features/RecentRooms";
import ResetPassword from "../auth/ResetPassword";
import SubscriptionRibbon from "../features/SubscriptionRibbon";
import { AuthState, UserContext } from "../contexts/UserContext";
import { ManageSubscription } from "../features/ManageSubscription";
import { CardDeckSettings } from "../features/CardDeckSettings";
import { CardDeckSettingsDialog } from "../features/CardDeckSettingsDialog";
import { Subscribe } from "../features/Subscribe";
import { SubscribeDialog } from "../features/SubscribeDialog";
import { SingInDialog } from "../auth/SignInDialog";
import { RequireAuth } from "../auth/RequireAuth";
import { usePlayer } from "../contexts/usePlayer";
import { SignUpDialog } from "../auth/SignUpDialog";
import { Box, Chip, ToggleButton, ToggleButtonGroup } from "@mui/material";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import AlignmentTrend from "../features/AlignmentTrend";
import { TrialExtensionRibbon } from "../features/TrialExtensionRibbon";
import { AlignmentTrendDialog } from "../features/AlignmentTrendDialog";
import { DebugMenu } from "../features/debugger/DebugMenu";
import { useEnvironment } from "../hooks/useEnvironment";
import RetroRoom from "../features/retro/RetroRoom";
import JoinRetroRoom from "../features/retro/JoinRetroRoom";
import CreateRetroRoom from "../features/retro/CreateRetroRoom";
import RecentRetroRooms from "../features/retro/RecentRetroRooms";
import { useConfig } from "../hooks/useConfig";
import { RetroMenu } from "../features/retro/RetroMenu";
import { RetroActionsProvider } from "../features/retro/RetroActionsContext";
import { RetroBetaInfoDialog } from "../features/retro/RetroBetaInfoDialog";
import NewBadge from "../components/NewBadge";

const RootContainer = styled(Box)({
  flexGrow: 1,
});

const TitleTypography = styled(Typography)({
  flexGrow: 1,
});

const ProductToggleButton = styled(ToggleButton)(({ theme }) => ({
  padding: theme.spacing(1, 3),
  gap: theme.spacing(1),
  fontWeight: 600,
  fontSize: 15,
  textTransform: 'none',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': { backgroundColor: theme.palette.primary.dark },
  },
}));

export default function UserApp(): ReactElement {
  const { authState } = useContext(UserContext);
  const { player } = usePlayer();
  const [isDevelopment] = useEnvironment();
  const config = useConfig();
  const location = useLocation();
  const isRetroRoute = location.pathname.startsWith('/retro/');
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isSignInDialogOpen, setIsSignInDialogOpen] = useState(false);
  const [isSignUpDialogOpen, setIsSignUpDialogOpen] = useState(false);
  const [isSubscribeDialogOpen, setIsSubscribeDialogOpen] = useState(false);
  const [alignmentTrendDialogOpen, setAlignmentTrendDialogOpen] = useState(false);
  const [alignmentTrendRoomId, setAlignmentTrendRoomId] = useState<string | null>(null);
  const [isBetaInfoOpen, setIsBetaInfoOpen] = useState(false);

  return (
    <RetroActionsProvider>
    <RootContainer>
      <AppBar position="sticky" enableColorOnDark color="primary">
        <SubscriptionRibbon />
        <TrialExtensionRibbon />
        <Toolbar>
          <TitleTypography variant="h6">
            {isRetroRoute ? 'Retro Room' : 'Story Point Poker'}
          </TitleTypography>
          {isRetroRoute && (
            <Chip
              label="Beta"
              size="small"
              color="info"
              onClick={() => setIsBetaInfoOpen(true)}
              sx={{ mr: 1, cursor: 'pointer' }}
            />
          )}
          {isRetroRoute && <RetroMenu />}
          {!(player || authState == AuthState.SignedIn || isRetroRoute) && <AboutButton />}
          {isDevelopment() && <DebugMenu />}
          <UserMenu
            player={player}
            onSettingsClicked={() => setIsSettingsDialogOpen(true)}
            onSignInClicked={() => setIsSignInDialogOpen(true)}
            onSignUpClicked={() => setIsSignUpDialogOpen(true)}
            onSignOutClicked={() => null}
            onManageSubscriptionClicked={() => {
              window.location.href = "/subscription";
            }}
            onRecentRoomsClicked={() => {
              window.location.href = "/";
            }}
            onSubscribeClicked={() => setIsSubscribeDialogOpen(true)}
            onAlignmentTrendClicked={(roomId) => {
              setAlignmentTrendRoomId(roomId);
              setAlignmentTrendDialogOpen(true);
            }}
          />
        </Toolbar>
      </AppBar>

      <Routes>
        <Route path="/:id/trend" element={<TrendPageWrapper />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/resetpassword" element={<ResetPassword email="" />} />
        <Route path="/about" element={<About />} />
        <Route path="/subscription" element={
          <RequireAuth>
            <ManageSubscription />
          </RequireAuth>
        } />
        <Route path="/subscribe" element={
          <RequireAuth>
            <Subscribe />
          </RequireAuth>
        } />
        <Route path="/:id/settings" element={<CardDeckSettings />} />
        {config.IS_RETRO_ENABLED && <Route path="/retro/:id" element={<RetroRoomWrapper />} />}
        <Route path="/:id" element={
          <>
            <RoomWrapper />
            {isSettingsDialogOpen && <CardDeckSettingsDialog open={isSettingsDialogOpen} onClosed={() => setIsSettingsDialogOpen(false)} />}
          </>
        } />
        <Route path="/" element={<CreateRoomWrapper />} />
      </Routes>
      {isSignInDialogOpen && <SingInDialog open={isSignInDialogOpen} onClosed={() => setIsSignInDialogOpen(false)} />}
      {isSignUpDialogOpen && <SignUpDialog open={isSignUpDialogOpen} onClosed={() => setIsSignUpDialogOpen(false)} />}
      {isSubscribeDialogOpen && <SubscribeDialog open={isSubscribeDialogOpen} onClosed={() => setIsSubscribeDialogOpen(false)} />}
      {isBetaInfoOpen && <RetroBetaInfoDialog open={isBetaInfoOpen} onClosed={() => setIsBetaInfoOpen(false)} />}
      {alignmentTrendRoomId && (
        <AlignmentTrendDialog 
          open={alignmentTrendDialogOpen} 
          roomUrlId={alignmentTrendRoomId}
          onClosed={() => {
            setAlignmentTrendDialogOpen(false);
            setAlignmentTrendRoomId(null);
          }}
        />
      )}
    </RootContainer>
    </RetroActionsProvider>
  );
}

//useParams only works for child components. this is why RoomContentWrapper is extracted from App
function TrendPageWrapper(): ReactElement {
  const { id: roomUrlId } = useParams<RoomParams>();
  const { track } = useTracking();
  
  useEffect(() => {
    if (roomUrlId) {
      track.event(Category.User, Action.AlignmentTrendVisited, roomUrlId);
    }
  }, [roomUrlId, track]);

  if (!roomUrlId) {
    return <div>Room ID is required</div>;
  }

  return <AlignmentTrend roomUrlId={roomUrlId} />;
}

function RoomWrapper(): ReactElement {
  const { id: roomUrlId } = useParams<RoomParams>();
  const { player, setPlayer } = useContext(PlayerContext);
  if (!roomUrlId) {
    throw new Error("Room URL ID is required");
  }
  const session = new SessionStorage().GetSession(roomUrlId);

  useEffect(() => {
    if (!player) {
      //console.log('update playerContext from local storage session after hard refresh')
      if (session) {
        //console.log('setPlayer from local storage(' + session.userId + ',' + session.userName + ');');
        setPlayer(new ContextPlayer(session.userId, session.userName, false));
      }
    }
  }, []);

  return session ? <Room roomUrlId={roomUrlId} /> : <JoinRoom roomUrlId={roomUrlId} />;
}

function RetroRoomWrapper(): ReactElement {
  const { id: roomUrlId } = useParams<RoomParams>();
  if (!roomUrlId) throw new Error('Retro Room URL ID is required');

  const session = new SessionStorage().GetSession(roomUrlId);

  if (!session) {
    return <JoinRetroRoom roomUrlId={roomUrlId} />;
  }

  return (
    <RetroRoom
      roomUrlId={roomUrlId}
      playerId={session.userId}
      playerName={session.userName}
    />
  );
}

function CreateRoomWrapper(): ReactElement {
  const { authState } = useContext(UserContext);
  const config = useConfig();
  const [alignmentTrendDialogOpen, setAlignmentTrendDialogOpen] = useState(false);
  const [alignmentTrendRoomId, setAlignmentTrendRoomId] = useState<string | null>(null);
  const [roomType, setRoomType] = useState<'poker' | 'retro'>('poker');

  return (
    <>
      {config.IS_RETRO_ENABLED && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4, px: 2 }}>
          <ToggleButtonGroup
            value={roomType}
            exclusive
            onChange={(_, newRoomType: 'poker' | 'retro') => {
              if (newRoomType !== null) setRoomType(newRoomType);
            }}
          >
            <ProductToggleButton value="poker">
              <StyleOutlinedIcon fontSize="small" />
              Planning Poker
            </ProductToggleButton>
            <ProductToggleButton
              value="retro"
              aria-label="Retrospective - new"
              sx={{ position: 'relative', overflow: 'visible' }}
            >
              <StickyNote2OutlinedIcon fontSize="small" />
              Retrospective
              <NewBadge />
            </ProductToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {roomType === 'poker' || !config.IS_RETRO_ENABLED ? <CreateRoom /> : <CreateRetroRoom />}

      {authState == AuthState.SignedIn && roomType === 'poker' && (
        <RecentRooms 
          onAlignmentTrendClicked={(roomId) => {
            setAlignmentTrendRoomId(roomId);
            setAlignmentTrendDialogOpen(true);
          }}
        />
      )}
      {authState == AuthState.SignedIn && roomType === 'retro' && config.IS_RETRO_ENABLED && (
        <RecentRetroRooms />
      )}
      {alignmentTrendRoomId && (
        <AlignmentTrendDialog 
          open={alignmentTrendDialogOpen} 
          roomUrlId={alignmentTrendRoomId}
          onClosed={() => {
            setAlignmentTrendDialogOpen(false);
            setAlignmentTrendRoomId(null);
          }}
        />
      )}
    </>
  );
}
